<template>
  <b-container class="mt-3">
    <router-link to="/settings/equipments" class="text-primary">
      Click to open Equipment settings
    </router-link>

    <hr />
    <b-row>
      <b-col>
        <label class="text-dark">Agency</label>
        <b-alert show variant="secondary">{{ divaltoAgency.name }}</b-alert>
      </b-col>
    </b-row>
    <b-row>
      <b-col>

        <label class="text-dark">Name</label>
        <b-alert show variant="secondary" >{{ project.name }}</b-alert>

      </b-col>
    </b-row>
    <b-alert show class="mt-3">
      <b-label>Settings</b-label>
 
      <b-form-group class="mt-3">
        <b-form-checkbox-group
         v-model="project.group_divalto_data"
          id="checkboxes-4"
          >
          <b-form-checkbox value="true">Group by divalto code</b-form-checkbox>
        </b-form-checkbox-group>
      </b-form-group>
      <i>
        <small>
          <b>Note :</b>: Lets you group equipments of type unit while preparing quote with divalto
        </small>
      </i>
    </b-alert>

    <b-alert show class="mt-3">
      <h3 class="mt-3">Grouping Rules</h3>
      <b-button size="sm" variant="primary" class="mb-3" @click="showRuleModal = true">
        <font-awesome-icon icon="plus" class="mr-1" />
        Add Rule
      </b-button>
      <p v-if="rules.length <= 0">Click above to add rules for this project</p>
      <b-table
        v-if="rules.length > 0"
        :items="rules"
        :fields="fields"
        responsive="sm"
        style="font-size: 0.875rem;"
      >
        <template #cell(delete)="row">
          <b-button-group size="sm">
            <b-button @click="openEditModal(row.item)" :disabled="ruleLoading">
              <font-awesome-icon :icon="['fas', 'edit']" />
            </b-button>
            <b-button variant="danger" @click="deleteRule(row.item.id)" :disabled="ruleLoading">
              <font-awesome-icon :icon="['fas', 'trash']" />
            </b-button>
          </b-button-group>
        </template>
      </b-table>
    </b-alert>

    <!-- Rule Creation Modal -->
    <b-modal v-model="showRuleModal" :title="isEditing ? 'Edit Grouping Rule' : 'Create Grouping Rule'" hide-footer @hide="resetNewRule">
      <b-form @submit.prevent="isEditing ? updateRule() : createRule()" class="rule-form">
        <b-form-group label="Rule Name:" label-for="rule-name">
          <b-form-input
            id="rule-name"
            v-model="newRule.name"
            type="text"
            placeholder="e.g., Small, Medium, Large"
            required
          ></b-form-input>
        </b-form-group>
        <b-form-group label="Min Perimeter (cm):" label-for="min-perimeter">
          <b-form-input
            id="min-perimeter"
            v-model.number="newRule.min_perimeter"
            type="number"
            step="0.01"
            placeholder="Leave blank for no lower limit"
          ></b-form-input>
        </b-form-group>
        <b-form-group label="Max Perimeter (cm):" label-for="max-perimeter">
          <b-form-input
            id="max-perimeter"
            v-model.number="newRule.max_perimeter"
            type="number"
            step="0.01"
            placeholder="Leave blank for no upper limit"
          ></b-form-input>
        </b-form-group>
        <div class="d-flex justify-content-end mt-3">
          <b-button type="submit" variant="primary" :disabled="ruleLoading" class="mr-2">
            {{ isEditing ? 'Update' : 'Create' }}
          </b-button>
          <b-button variant="secondary" @click="showRuleModal = false">Cancel</b-button>
        </div>
      </b-form>
    </b-modal>

    <b-row class="mt-3">
      <b-col>
        <b-btn variant="outline-dark rounded-lg" @click="closeSidebar()">ANNULER</b-btn>
        <b-btn class="ml-2 rounded-lg" variant="primary" :disabled="loading" @click="submitHandler()">
          UPDATE
          <b-spinner v-if="loading" small label="Loading..."></b-spinner>
        </b-btn>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { ProjectMixin } from '@/mixins';
import { mapGetters } from 'vuex';

export default {
  name: "ProjectSetting",
  mixins: [ProjectMixin],

  data() {
    return {
      loading: false,
      ruleLoading: false,
      showRuleModal: false,
      rules: [],
      newRule: { name: "", min_perimeter: null, max_perimeter: null },
      fields: [
        { key: 'name', label: 'Rule Name' },
        { key: 'min_perimeter', label: 'Min Perimeter' },
        { key: 'max_perimeter', label: 'Max Perimeter' },
        { key: 'delete', label: 'Actions' },
      ],
    };
  },

  computed: {
    ...mapGetters({
      project: 'projects/item',
      divaltoAgency: 'divaltoAgencies/item'
    }),
  },

  mounted() {
    if (this.project.divalto_agency_id) {
      this.$store.dispatch('divaltoAgencies/find', { id: this.project.divalto_agency_id });
    }
    this.fetchRules();
  },

  methods: {
    submitHandler() {
      this.loading = true;
      this.$store
        .dispatch('projects/update', {
          id: this.project.id,
          payload: {
            project: {
              group_divalto_data: this.project.group_divalto_data,
            }
          }
        })
        .then(() => this.closeSidebar())
        .finally(() => {
          this.loading = false;
        });
    },

    fetchRules() {
      return this.$store
        .dispatch("groupingRules/fetchRules", {
          scope: "project",
          id: this.project.id
        })
        .then(() => {
          this.rules = this.$store.state.groupingRules.rules.data;
        })
        .catch((err) => {
          console.error("Error fetching rules:", err);
        });
    },

    createRule() {
      this.ruleLoading = true;
      const ruleData = {
        grouping_rule: {
          project_id: this.project.id,
          name: this.newRule.name,
          min_perimeter: this.newRule.min_perimeter,
          max_perimeter: this.newRule.max_perimeter,
        }
      };

      this.$store
        .dispatch("groupingRules/createRule", {
          scope: "project",
          id: this.project.id,
          ruleData
        })
        .then(() => {
          this.fetchRules();
          this.resetNewRule();
          this.showRuleModal = false;
        })
        .catch((err) => {
          if (err.response && err.response.data && err.response.data.errors) {
            const errors = err.response.data.errors;
            const messages = [];
            for (const field in errors) {
              messages.push(`${field}: ${errors[field].join(", ")}`);
            }
            alert("Error:\n" + messages.join("\n"));
          } else {
            alert("Error creating rule: " + (err.message || "Unknown error"));
          }
        })
        .finally(() => {
          this.ruleLoading = false;
        });
    },
    updateRule() {
      this.ruleLoading = true;
      const ruleData = {
        grouping_rule: {
          project_id: this.project.id,
          name: this.newRule.name,
          min_perimeter: this.newRule.min_perimeter,
          max_perimeter: this.newRule.max_perimeter,
        }
      };

      this.$store
        .dispatch("groupingRules/updateRule", {
          scope: "project",
          id: this.project.id,
          ruleId: this.newRule.id,
          payload: ruleData
        })
        .then(() => {
          this.fetchRules();
          this.resetNewRule();
          this.showRuleModal = false;
        })
        .catch((err) => {
          if (err.response?.data?.errors) {
            const errors = err.response.data.errors;
            const messages = [];
            for (const field in errors) {
              messages.push(`${field}: ${errors[field].join(", ")}`);
            }
            alert("Error:\n" + messages.join("\n"));
          } else {
            alert("Error updating rule: " + (err.message || "Unknown error"));
          }
        })
        .finally(() => {
          this.ruleLoading = false;
        });
    },

    deleteRule(ruleId) {
      if (confirm("Are you sure you want to delete this rule?")) {
        this.ruleLoading = true;
        this.$store
          .dispatch("groupingRules/deleteRule", {
            scope: "project",
            id: this.project.id,
            ruleId,
          })
          .then(() => {
            this.fetchRules();
          })
          .catch((error) => {
            console.error("Error deleting rule:", error);
          })
          .finally(() => {
            this.ruleLoading = false;
          });
      }
    },

    resetNewRule() {
      this.isEditing = false;
      this.newRule = { name: "", min_perimeter: null, max_perimeter: null };
    },

    formatRange(min, max) {
      if (min === null && max === null) return "Any";
      if (min === null) return `≤ ${this.formatValue(max)} cm`;
      if (max === null) return `≥ ${this.formatValue(min)} cm`;
      return `${this.formatValue(min)} - ${this.formatValue(max)} cm`;
    },

    formatValue(value) {
      return typeof value === "number" ? value.toFixed(2) : "0.00";
    },
    openEditModal(rule) {
      this.newRule = { ...rule };
      this.isEditing = true;
      this.showRuleModal = true;
    }
  },
};
</script>

<style scoped>
.rule-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}
</style>
